import { readMetaRetryAfterSeconds } from './metaAdsConfig';
import { formatMetaMoney } from './metaAdsMoney';
import { metaWriteVisible, metaWriteWarningText, parseMetaBudgetInput, readMetaWriteError } from './metaWrite';
import { maskMetaAdAccountId } from './metaWriteLog';

/*
 * Crear campañas en Meta desde la ficha (F2b API, `argenlotapi/docs/frontend/FRONTEND_META_CREAR.md`).
 * Puro: quién ve el botón, lectura de `GET /getmetacreateoptions` y de `POST /createmetacampaign`
 * (preview y confirmar), el formulario (valores iniciales, validación, cuerpo) y los textos.
 * Todo nace en pausa; el cuerpo del preview y el de confirmar son el MISMO (más `expected` + key).
 */

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const idStr = (v) => (v == null ? null : str(String(v)));
const obj = (v) => (v && typeof v === 'object' ? v : null);
const list = (v) => (Array.isArray(v) ? v : []);
const num = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const range = (v, fallback) => {
  const a = list(v).map(Number);
  return a.length === 2 && a.every(Number.isFinite) ? a : fallback;
};

/** Registros con los que se puede crear (§4.0). El lote de catastro no. */
export const META_CREATE_KINDS = ['user_lot', 'cad_parcel'];

/**
 * ¿La ficha muestra «Crear campaña»? Solo el dueño en su propio espacio, con Meta listo, la
 * escritura visible y `features.create_campaigns === 'available'` (la API decide; no se deduce).
 * Si no, el botón NO se muestra (decisión de Ignacio: nada de «Próximamente»).
 */
export function metaCreateAvailable({ connection, ownerUserId = null, itemKind } = {}) {
  if (!META_CREATE_KINDS.includes(itemKind)) return false;
  return metaCreateAccess({ connection, ownerUserId });
}

/**
 * ¿Este usuario puede crear campañas en este espacio? (sin mirar el registro). Lo usa el ícono
 * «Crear campaña» del panel; la ficha suma el tipo de registro (`metaCreateAvailable`).
 */
export function metaCreateAccess({ connection, ownerUserId = null } = {}) {
  if (!connection || ownerUserId) return false;
  if (connection.viewerRole !== 'owner') return false;
  if (connection.createCampaigns !== 'available') return false;
  return metaWriteVisible(connection.write) && Boolean(connection.write?.viewerCanWrite);
}

function readAccount(raw) {
  const id = idStr(raw?.ad_account_id)?.replace(/^act_/, '');
  if (!id) return null;
  const name = str(raw.name);
  const masked = maskMetaAdAccountId(id);
  return {
    id,
    name,
    masked,
    label: name ? `${name} ${masked}` : masked,
    currency: str(raw.currency)?.toUpperCase() || null,
    realMoney: typeof raw.real_money === 'boolean' ? raw.real_money : null,
    paymentStatus: str(raw.payment_status),
  };
}

/** `data` de `GET /getmetacreateoptions` (§4.1). */
export function readMetaCreateOptions(res) {
  const data = obj(res?.data) || {};
  const record = obj(data.record) || {};
  const defaults = obj(data.defaults) || {};
  const limits = obj(data.limits) || {};
  const accounts = list(data.accounts).map(readAccount).filter(Boolean);
  const selected = idStr(data.selected_ad_account_id)?.replace(/^act_/, '') || null;
  const photos = list(data.photos)
    .map((p) => ({ id: idStr(p?.image_id), url: str(p?.url), isCover: p?.is_cover === true }))
    .filter((p) => p.id);
  return {
    record: {
      itemKind: str(record.item_kind),
      id: idStr(record.id),
      title: str(record.title),
      hasPin: record.has_pin !== false,
      photosCount: num(record.photos_count) ?? photos.length,
    },
    accounts,
    selectedAdAccountId: selected,
    pages: list(data.pages)
      .map((p) => ({ id: idStr(p?.page_id), name: str(p?.name), canAdvertise: typeof p?.can_advertise === 'boolean' ? p.can_advertise : null }))
      .filter((p) => p.id),
    pageSource: str(data.page_source),
    photos,
    defaults: {
      name: str(defaults.name) || '',
      headline: str(defaults.headline) || '',
      message: str(defaults.message) || '',
      link: str(defaults.link),
      radiusKm: num(defaults.radius_km) ?? 10,
      ageMin: num(defaults.age_min) ?? 18,
      dailyBudget: str(defaults.daily_budget == null ? null : String(defaults.daily_budget)),
      currency: str(defaults.currency)?.toUpperCase() || null,
    },
    limits: {
      dailyBudgetMax: num(limits.daily_budget_max),
      dailyBudgetMin: num(limits.daily_budget_min),
      radiusKm: range(limits.radius_km, [1, 80]),
      ageMin: range(limits.age_min, [18, 65]),
      headlineMax: num(limits.headline_max) ?? 100,
      messageMax: num(limits.message_max) ?? 1000,
      creationsLeftToday: num(limits.creations_left_today),
      creationsPerDay: num(limits.creations_per_day),
    },
    warnings: list(data.warnings).filter((w) => str(w?.code)),
    canCreate: data.can_create === true,
    blockedReason: str(data.blocked_reason),
    retryAfterS: num(data.retry_after_s),
  };
}

const BLOCKED_TEXTS = {
  not_owner: 'Solo el dueño puede crear campañas.',
  not_shared: 'Compartí el link público primero.',
  share_restricted: 'El link pide iniciar sesión. Pasalo a público para crear.',
  link_unavailable: 'No disponible por ahora.',
  no_photo: 'Subí una foto para crear la campaña.',
  no_pin: 'Ubicá la unidad en el mapa para crear la campaña.',
  no_account: 'Habilitá una cuenta en Configuración de Meta.',
  no_page: 'La cuenta no tiene una página de Facebook para anunciar.',
  daily_limit: 'Ya creaste las campañas de hoy. Probá mañana.',
  kind_not_supported: 'No se pueden crear campañas para este registro.',
};

/** Por qué no se puede crear (`blocked_reason`, §4.1). Los motivos de F2a caen en uno genérico. */
export function metaCreateBlockedText(reason) {
  if (!reason) return null;
  return BLOCKED_TEXTS[reason] || 'Esta cuenta no se puede usar para crear campañas.';
}

const pickPage = (pages, current) => {
  if (current && pages.some((p) => p.id === current)) return current;
  return (pages.find((p) => p.canAdvertise === true) || pages[0])?.id || '';
};
const pickPhoto = (photos, current) => {
  if (current && photos.some((p) => p.id === current)) return current;
  return (photos.find((p) => p.isCover) || photos[0])?.id || '';
};
const budgetInput = (value) => (value == null ? '' : String(value).replace('.', ','));

/**
 * Valores iniciales del formulario (todo pre-armado desde la ficha). `prev` = el formulario de antes
 * de cambiar de cuenta: se conservan los textos, fechas y opciones avanzadas; la página, la foto y el
 * presupuesto (si cambió la moneda) se rearman con la cuenta nueva.
 */
export function metaCreateInitialForm(options, prev = null) {
  const d = options?.defaults || {};
  const sameCurrency = prev && prev.currency && prev.currency === d.currency;
  return {
    adAccountId: options?.selectedAdAccountId || '',
    pageId: pickPage(options?.pages || [], prev?.pageId),
    imageId: pickPhoto(options?.photos || [], prev?.imageId),
    name: prev ? prev.name : d.name || '',
    headline: prev ? prev.headline : d.headline || '',
    message: prev ? prev.message : d.message || '',
    dailyBudget: sameCurrency && prev.dailyBudget ? prev.dailyBudget : budgetInput(d.dailyBudget),
    currency: d.currency || null,
    startDate: prev ? prev.startDate : '',
    endDate: prev ? prev.endDate : '',
    radiusKm: prev ? prev.radiusKm : String(d.radiusKm ?? 10),
    ageMin: prev ? prev.ageMin : String(d.ageMin ?? 18),
  };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const addYears = (iso, years) => `${Number(iso.slice(0, 4)) + years}${iso.slice(4)}`;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;
const intIn = (value, [min, max]) => {
  const s = String(value ?? '').trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= min && n <= max ? n : null;
};

/**
 * Validación local (la API valida igual). `today` = `YYYY-MM-DD`.
 * @returns {Object<string, string>} campo → mensaje (vacío = todo bien).
 */
export function validateMetaCreateForm(form, limits = {}, today = '') {
  const errors = {};
  const f = form || {};
  const text = (v) => String(v ?? '').trim();
  if (!f.adAccountId) errors.adAccountId = 'Elegí una cuenta.';
  if (!f.pageId) errors.pageId = 'Elegí una página.';
  if (!f.imageId) errors.imageId = 'Elegí una foto.';
  const name = text(f.name);
  if (!name || name.length > 400 || CONTROL.test(name)) errors.name = 'Escribí un nombre de hasta 400 caracteres.';
  const hMax = limits.headlineMax ?? 100;
  const headline = text(f.headline);
  if (!headline || headline.length > hMax || CONTROL.test(headline)) errors.headline = `Escribí un título de hasta ${hMax} caracteres.`;
  const mMax = limits.messageMax ?? 1000;
  const message = text(f.message);
  if (!message || message.length > mMax || CONTROL.test(message)) errors.message = `Escribí un texto de hasta ${mMax} caracteres.`;
  const amount = parseMetaBudgetInput(f.dailyBudget);
  if (amount == null) {
    errors.dailyBudget = 'Escribí un monto mayor a 0.';
  } else if (limits.dailyBudgetMin != null && Number(amount) < limits.dailyBudgetMin) {
    errors.dailyBudget = `Mínimo ${formatMetaMoney(limits.dailyBudgetMin, f.currency)} por día.`;
  } else if (limits.dailyBudgetMax != null && Number(amount) > limits.dailyBudgetMax) {
    errors.dailyBudget = `Máximo ${formatMetaMoney(limits.dailyBudgetMax, f.currency)} por día.`;
  }
  const start = text(f.startDate);
  const end = text(f.endDate);
  if (start && (!ISO_DAY.test(start) || (today && start < today))) errors.startDate = 'Desde hoy en adelante.';
  if (end) {
    const from = start && ISO_DAY.test(start) ? start : today;
    if (!ISO_DAY.test(end) || (from && end <= from)) errors.endDate = 'Tiene que ser después del inicio.';
    else if (today && end > addYears(today, 3)) errors.endDate = 'Hasta 3 años.';
  }
  if (intIn(f.radiusKm, limits.radiusKm || [1, 80]) == null) {
    const [a, b] = limits.radiusKm || [1, 80];
    errors.radiusKm = `De ${a} a ${b} km.`;
  }
  if (intIn(f.ageMin, limits.ageMin || [18, 65]) == null) {
    const [a, b] = limits.ageMin || [18, 65];
    errors.ageMin = `De ${a} a ${b} años.`;
  }
  return errors;
}

/** Cuerpo de `POST /createmetacampaign` (el mismo para el preview y para confirmar). */
export function buildMetaCreateBody({ itemKind, id }, form) {
  const f = form || {};
  const date = (v) => (ISO_DAY.test(String(v || '').trim()) ? String(v).trim() : null);
  return {
    record: { item_kind: itemKind, id },
    ad_account_id: f.adAccountId,
    page_id: f.pageId,
    image_id: f.imageId,
    name: String(f.name ?? '').trim(),
    headline: String(f.headline ?? '').trim(),
    message: String(f.message ?? '').trim(),
    daily_budget: parseMetaBudgetInput(f.dailyBudget) ?? '',
    currency: f.currency || '',
    start_date: date(f.startDate),
    end_date: date(f.endDate),
    radius_km: Number(String(f.radiusKm ?? '').trim()),
    age_min: Number(String(f.ageMin ?? '').trim()),
  };
}

/** Clave estable del cuerpo: si cambia algo después del preview, hay que revisar de nuevo. */
export const metaCreateBodyKey = (body) => JSON.stringify(body || null);

/** Respuesta del preview (`dry_run`, §4.2). `expected` es un texto (sha256), no un objeto como en F2a. */
export function readMetaCreateDryRun(res) {
  const data = obj(res?.data) || {};
  const p = obj(data.preview) || {};
  return {
    preview: {
      text: str(p.text),
      budgetText: str(p.budget_text),
      maxAllowed: num(p.max_allowed),
      minAllowed: num(p.min_allowed),
      warnings: list(p.warnings).filter((w) => str(w?.code)),
      realMoney: typeof p.real_money === 'boolean' ? p.real_money : null,
    },
    expected: str(data.expected),
  };
}

/** Respuesta `200` de confirmar (§4.3). */
export function readMetaCreateResult(res) {
  const data = obj(res?.data) || {};
  return {
    createId: idStr(data.create_id),
    status: str(data.status),
    replayed: data.replayed === true,
    parts: obj(data.parts) || {},
    campaignId: idStr(data.campaign_id),
    adsetId: idStr(data.adset_id),
    adId: idStr(data.ad_id),
    linked: list(data.linked_records).length > 0,
    refreshed: data.refreshed !== false,
    refreshNote: str(data.refresh_note),
    warnings: list(data.warnings).filter((w) => str(w?.code)),
    metaUrl: str(data.meta_url),
  };
}

// Campo del API → campo del formulario.
const FIELD_MAP = {
  ad_account_id: 'adAccountId', page_id: 'pageId', image_id: 'imageId', name: 'name', headline: 'headline', message: 'message',
  daily_budget: 'dailyBudget', currency: 'dailyBudget', start_date: 'startDate', end_date: 'endDate', radius_km: 'radiusKm', age_min: 'ageMin',
};
export const META_CREATE_GENERIC_MESSAGE = 'No pudimos crear la campaña. Revisala en Meta antes de probar de nuevo.';

// Textos de §9.10 (avisos y errores de Meta con texto propio).
export const META_CREATE_PAYMENT_MESSAGE = 'Esta cuenta no tiene medio de pago. Lo creado queda en pausa; para crear el anuncio cargá uno en Meta y reintentá, o eliminá lo creado.';
// Antes de crear la campaña no quedó nada: sin «lo creado».
export const META_CREATE_PAYMENT_BEFORE_MESSAGE = 'Esta cuenta no tiene medio de pago. Cargá uno en Meta y reintentá.';
export const META_CREATE_VERIFICATION_MESSAGE = 'Meta pide verificar tu cuenta antes de publicar anuncios. Verificala en Meta y reintentá.';
export const META_CREATE_PARTIAL_MESSAGE = 'Se creó una parte de la campaña y quedó en pausa. Podés reintentar o eliminar lo creado.';
export const META_CREATE_UNCONFIRMED_MESSAGE = 'Meta no contestó y no sabemos qué se creó. Revisalo en Meta.';
export const META_CREATE_KEY_REJECTED_MESSAGE = 'No se puede retomar: la ficha cambió desde el intento. Eliminá lo creado o revisalo en Meta.';
export const META_CREATE_KEY_REUSED_MESSAGE = 'Ese intento ya no se puede retomar. Eliminá lo creado o revisalo en Meta.';
export const META_CREATE_IN_PROGRESS_MESSAGE = 'Esta creación sigue en curso. Probá en un momento.';

// «Eliminar lo creado» con la campaña todavía sin sincronizar (404 META_CAMPAIGN_NOT_FOUND en el preview).
export const META_CREATE_DELETE_NOT_SYNCED_MESSAGE = 'Todavía no la vemos en Terrio. Sincronizá y volvé a intentar.';

/** Ads Manager de la cuenta (o el general) cuando el pendiente no trae `meta_url`. */
export function metaAdsManagerUrl(adAccountId) {
  const act = String(adAccountId || '').replace(/^act_/, '');
  const base = 'https://adsmanager.facebook.com/adsmanager/manage/campaigns';
  return /^\d+$/.test(act) ? `${base}?act=${act}` : base;
}

/** «Quedó sin vincular a esta unidad hasta que se cree el anuncio.» (parcial sin anuncio, §9.10). */
export const metaCreateUnlinkedText = (noun = { this: 'esta unidad' }) => `Quedó sin vincular a ${noun.this} hasta que se cree el anuncio.`;

// Pasos de la creación, en orden (§4.3: imagen → campaña → conjunto → creativo → anuncio).
export const META_CREATE_STEPS = [
  { key: 'image', label: 'Imagen' },
  { key: 'campaign', label: 'Campaña' },
  { key: 'adset', label: 'Conjunto' },
  { key: 'creative', label: 'Creativo' },
  { key: 'ad', label: 'Anuncio' },
];
const PART_STATES = ['created', 'failed', 'not_started', 'unknown'];

/**
 * `parts` del API → lista ordenada `{ key, label, state, id }`. Los pasos que no vinieron no se
 * listan (el ejemplo de parcial no trae la imagen). Estado desconocido → `unknown`.
 */
export function readMetaCreateParts(raw) {
  const parts = obj(raw) || {};
  return META_CREATE_STEPS
    .filter((s) => obj(parts[s.key]))
    .map((s) => {
      const p = parts[s.key];
      return { key: s.key, label: s.label, state: PART_STATES.includes(p.state) ? p.state : 'unknown', id: idStr(p.id) };
    });
}

/** ¿Se llegó a crear el anuncio? (si no, la campaña queda sin vincular). */
export const metaCreateHasAd = (parts) => list(parts).some((p) => p.key === 'ad' && p.state === 'created');

/** Texto del parcial según la causa, con «sin vincular» si falta el anuncio. */
export function metaCreatePartialText({ causeCode = null, parts = [] } = {}, noun = { this: 'esta unidad' }) {
  let text = META_CREATE_PARTIAL_MESSAGE;
  if (causeCode === 'META_NO_PAYMENT_METHOD') text = META_CREATE_PAYMENT_MESSAGE;
  else if (causeCode === 'META_ACCOUNT_VERIFICATION_REQUIRED') text = META_CREATE_VERIFICATION_MESSAGE;
  return metaCreateHasAd(parts) ? text : `${text} ${metaCreateUnlinkedText(noun)}`;
}

// Estado parcial (opción A): qué falta según el paso que falló.
const PARTIAL_STEP_TITLES = {
  image: 'Falta subir la imagen',
  campaign: 'Falta crear la campaña',
  adset: 'Falta crear el conjunto',
  creative: 'Falta crear el creativo',
  ad: 'Falta publicar el anuncio',
};
export const META_CREATE_PARTIAL_TITLE = 'Quedó a medias';
export const META_CREATE_PARTIAL_PAYMENT_CAUSE = 'Esta cuenta no tiene medio de pago.';
export const META_CREATE_PARTIAL_VERIFICATION_CAUSE = 'Meta pide verificar tu cuenta.';
export const META_CREATE_PARTIAL_GENERIC_CAUSE = 'Podés reintentar desde el paso que falló o eliminar lo creado.';

/**
 * Lo que muestra el parcial (opción A del mockup), sin tocar la lógica de T3:
 * `title` («Falta publicar el anuncio», de `failedStep` o del primer paso no creado), `progress`
 * («4 de 5 pasos listos · en pausa · sin vincular a esta unidad»), `tone` (`warn` solo por pago o
 * verificación; si no, `neutral`), `cause` (corto; si ya no se retoma, el `message` del bloqueo) y
 * `linkLabel` del link a Meta.
 */
export function metaCreatePartialView(partial, noun = { this: 'esta unidad' }) {
  const parts = list(partial?.parts);
  const step = partial?.failedStep
    || parts.find((p) => p.state === 'failed')?.key
    || parts.find((p) => p.state !== 'created')?.key
    || null;
  const title = PARTIAL_STEP_TITLES[step] || META_CREATE_PARTIAL_TITLE;
  const done = parts.filter((p) => p.state === 'created').length;
  const progress = [
    parts.length ? `${done} de ${parts.length} pasos listos` : null,
    parts.length ? 'en pausa' : 'En pausa',
    metaCreateHasAd(parts) ? null : `sin vincular a ${noun.this}`,
  ].filter(Boolean).join(' · ');
  const cause = partial?.causeCode;
  // Ya no se retoma: manda el mensaje del bloqueo (neutro), no la causa del primer intento.
  const blocked = partial?.retryable === false;
  const payment = !blocked && cause === 'META_NO_PAYMENT_METHOD';
  const verification = !blocked && cause === 'META_ACCOUNT_VERIFICATION_REQUIRED';
  let text = META_CREATE_PARTIAL_GENERIC_CAUSE;
  if (blocked) text = partial?.message || META_CREATE_KEY_REUSED_MESSAGE;
  else if (payment) text = META_CREATE_PARTIAL_PAYMENT_CAUSE;
  else if (verification) text = META_CREATE_PARTIAL_VERIFICATION_CAUSE;
  let linkLabel = 'Ver en Meta';
  if (verification) linkLabel = 'Verificar en Meta';
  else if (payment) linkLabel = 'Cargar medio de pago';
  return { title, progress, tone: payment || verification ? 'warn' : 'neutral', cause: text, linkLabel };
}

/**
 * Error del preview o de confirmar → qué muestra el diálogo. Extiende `readMetaWriteError` (F2a).
 * `kind`: field (debajo del campo) | stale | rate_limited | partial | unconfirmed | blocked | unavailable |
 * payment | verification | key_rejected | key_reused | in_progress | los de F2a (owner_only, disabled,
 * permission, not_enabled, network, generic).
 * Siempre trae `createId`, `campaignId`, `parts`, `failedStep` y `metaUrl` si el API los mandó:
 * con `createId` y sin `campaignId` la falla fue ANTES de la campaña (se reintenta con key nueva).
 */
export function readMetaCreateError(res, currency = null, noun = { this: 'esta unidad' }) {
  const base = readMetaWriteError(res);
  const data = obj(res?.data) || {};
  const field = (k) => (res?.[k] !== undefined ? res[k] : data[k]);
  const extra = {
    createId: idStr(field('create_id')),
    campaignId: idStr(field('campaign_id')),
    adsetId: idStr(field('adset_id')),
    adId: idStr(field('ad_id')),
    parts: readMetaCreateParts(field('parts')),
    failedStep: str(field('failed_step')),
    causeCode: str(field('cause_code'))?.toUpperCase() || null,
    retryAfterS: num(field('retry_after_s')),
  };
  const e = { ...base, ...extra };
  switch (base.code) {
    case 'META_WRITE_STALE':
      return { ...e, kind: 'stale', message: 'Cambió algo. Revisá y confirmá de nuevo.', expected: str(field('expected')) };
    case 'META_CREATE_LIMIT':
      return { ...e, kind: 'rate_limited', message: 'Ya creaste las campañas de hoy', retryAfterS: readMetaRetryAfterSeconds(res, 3600) };
    case 'META_RATE_LIMITED':
      return { ...e, retryAfterS: readMetaRetryAfterSeconds(res, 60) };
    case 'META_CREATE_PARTIAL':
      return { ...e, kind: 'partial', message: metaCreatePartialText(extra, noun) };
    case 'META_WRITE_UNCONFIRMED':
      return { ...e, kind: 'unconfirmed', message: META_CREATE_UNCONFIRMED_MESSAGE };
    case 'META_NO_PAYMENT_METHOD':
      return { ...e, kind: 'payment', message: extra.campaignId ? META_CREATE_PAYMENT_MESSAGE : META_CREATE_PAYMENT_BEFORE_MESSAGE };
    case 'META_ACCOUNT_VERIFICATION_REQUIRED':
      return { ...e, kind: 'verification', message: META_CREATE_VERIFICATION_MESSAGE };
    case 'META_WRITE_IN_PROGRESS':
      return { ...e, kind: 'in_progress', message: META_CREATE_IN_PROGRESS_MESSAGE };
    case 'META_WRITE_KEY_REUSED':
      return { ...e, kind: 'key_reused', message: META_CREATE_KEY_REUSED_MESSAGE };
    case 'META_PAGE_NOT_ALLOWED':
      return { ...e, kind: 'field', field: 'pageId', message: 'Esta página no se puede usar con la cuenta. Elegí otra.' };
    case 'META_RECORD_NOT_LINKABLE': {
      const reason = str(field('reason'));
      return { ...e, kind: 'blocked', reason, message: metaCreateBlockedText(reason) || base.message };
    }
    case 'META_CREATE_NOT_MIGRATED':
      return { ...e, kind: 'unavailable', message: 'No disponible por ahora.' };
    case 'META_WRITE_BUDGET_INVALID': {
      let message = 'Revisá el presupuesto.';
      if (base.reason === 'above_ceiling' && base.maxAllowed != null) message = `Máximo ${formatMetaMoney(base.maxAllowed, currency)} por día.`;
      else if (base.reason === 'meta_rejected' && base.minAllowed != null) message = `Mínimo ${formatMetaMoney(base.minAllowed, currency)} por día.`;
      else if (base.reason === 'currency_without_ceiling') message = 'Esta moneda no tiene tope en Terrio: no se puede crear.';
      return { ...e, kind: 'field', field: 'dailyBudget', message };
    }
    case 'VALIDATION_ERROR': {
      // Mismo key con otro cuerpo (la ficha cambió): un parcial ya no se puede retomar.
      if (base.field === 'idempotency_key') return { ...e, kind: 'key_rejected', message: META_CREATE_KEY_REJECTED_MESSAGE };
      const mapped = FIELD_MAP[base.field];
      if (mapped) return { ...e, kind: 'field', field: mapped, message: base.message || 'Revisá este dato.' };
      return { ...e, kind: 'generic', message: base.message || META_CREATE_GENERIC_MESSAGE };
    }
    default:
      if (base.kind === 'generic') return { ...e, message: base.message || META_CREATE_GENERIC_MESSAGE };
      return e;
  }
}

// Sin medio de pago primero (ámbar), después el resto. `CREATED_PAUSED` no va acá: el resumen ya
// dice «Se crea en pausa». `REAL_MONEY` tampoco: activarla gasta, y eso ya se entiende.
const NOTICE_ORDER = ['NO_PAYMENT_METHOD', 'PAYMENT_UNKNOWN', 'PAGE_UNVERIFIED', 'TEXT_LONG', 'IMAGE_SIZE'];
const MONEY = ['NO_PAYMENT_METHOD'];

/**
 * UN solo aviso con los `code` de las opciones o del preview. Ámbar si falta el medio de pago;
 * si no, teal. `REAL_MONEY` no se muestra. `null` = sin aviso.
 * @returns {{ tone: 'warn'|'info', text: string, codes: string[] }|null}
 */
export function metaCreateNotice(warnings) {
  const all = list(warnings).filter((w) => str(w?.code) && w.code !== 'CREATED_PAUSED');
  const sorted = [
    ...NOTICE_ORDER.map((code) => all.find((w) => w.code === code)).filter(Boolean),
    ...all.filter((w) => !NOTICE_ORDER.includes(w.code)),
  ];
  const parts = [];
  const codes = [];
  sorted.forEach((w) => {
    const t = metaWriteWarningText(w, 'campaign', 'create');
    if (t && !codes.includes(w.code)) { parts.push(t); codes.push(w.code); }
  });
  if (!parts.length) return null;
  return { tone: codes.some((c) => MONEY.includes(c)) ? 'warn' : 'info', text: parts.join(' '), codes };
}

/** «Campaña creada en pausa y vinculada a esta unidad.» (§9.4). */
export function metaCreateSuccessText(result, noun = { this: 'esta unidad' }) {
  if (result?.linked) return `Campaña creada en pausa y vinculada a ${noun.this}.`;
  return 'Campaña creada en pausa.';
}
